import { useRouter } from 'next/router';

import { useSubscriptionStore } from '@/_app/providers/SubscriptionLockScreenProvider/useSubscriptionStore';
import { ApiAuthHttpResponse, useApi } from '@/shared/api';
import { getBearerToken } from '@/shared/api/utils';
import { AccessTokenPayload, extractTokenPayload } from '@/shared/helpers/auth';
import { useAppLogout } from '@/shared/hooks/useAppLogout';
import { useInitProfile } from '@/shared/hooks/useInitProfile';
import { useAuthStore } from '@/shared/store/auth.store';
import { getProfileStateProps, useProfileStore } from '@/shared/store/profile.store';
import { ERegisrationStep } from '@/shared/store/registration.store';

import { useAuthFlowStore } from './authFlow.store';

export const useAuthFlow = () => {
  const router = useRouter();
  const api = useApi();

  const authStore = useAuthStore();
  const { setIsSubscriptionActive } = useSubscriptionStore();
  const profileStore = useProfileStore();
  const authMainUserStore = useAuthFlowStore();
  const { initProfileStore } = useInitProfile();
  const { appLogout } = useAppLogout();

  const fetchAuthGrant = async (email: string, password: string) => {
    const { data: authData } = await api.authLogin({ email, password });

    return authData ? authData.accessToken : null;
  };

  const fetchGoogleAuth = async (code: string) => {
    const { data: authData } = await api.authGoogleVerifyCode({ code });

    return authData ? authData.accessToken : null;
  };

  const fetchAppLogin = async (accessToken: string): Promise<ApiAuthHttpResponse | null> => {
    const res = await api.loginViaPersonalAccountCode({ code: accessToken });

    if (!res.data?.account.profile) {
      return null;
    }

    if (res.error) {
      return null;
    }

    return res.data;
  };

  const appLogin = async (accessToken: string) => {
    const loginData = await fetchAppLogin(accessToken);
    const profile = loginData?.account.profile;

    if (!loginData || !profile || !profile.encryptionTypeId) return;

    authMainUserStore.setAccessToken(accessToken);
    authMainUserStore.setLegacyToken(loginData.token);
    authMainUserStore.setAccount({
      id: loginData.account.id,
      email: loginData.account.email,
      profile: { userName: loginData.account.profile?.userName ?? '' },
    });

    const resMe = await api.authMe(getBearerToken(accessToken));

    authMainUserStore.setSchemaVersion(loginData.account.schemaVersion);
    authMainUserStore.setProfileData({
      ...getProfileStateProps(profile),
      hasCompletedOnboarding: resMe?.data?.hasCompletedOnboarding ?? false,
    });

    const isRegistration = profile.status !== 'ACTIVE';

    if (profile.status === 'ACTIVE') {
      const { data: subscriptionStatusData } = await api.subscriptionGetStatus({
        headers: { authorization: loginData.token },
      });

      if (subscriptionStatusData?.status !== 'ACTIVE') {
        setIsSubscriptionActive({ isActive: false, isRegistered: !isRegistration });
        return;
      }
    }

    if (isRegistration) {
      await router.push(`/auth/register?step=${ERegisrationStep.CREATE_SEED}`);
    } else {
      await router.push('/auth/login/enter-mnemonic', undefined);
    }
  };

  const initStores = () => {
    if (authMainUserStore.legacyToken && authMainUserStore.account) {
      authStore.setLegacyToken(authMainUserStore.legacyToken);
      authStore.setAccount(authMainUserStore.account);
    }
    if (authMainUserStore.accessToken) {
      authStore.setAccessToken(authMainUserStore.accessToken);
    }

    if (authMainUserStore.schemaVersion) {
      authStore.setSchemaVersion(authMainUserStore.schemaVersion);
    }

    if (authMainUserStore.profile) {
      initProfileStore(authMainUserStore.profile);
    }
  };

  const finishLogin = () => {
    initStores();
    authMainUserStore.clear();
  };

  const finishRegistration = (publicKey: string) => {
    initStores();
    profileStore.setPublicKey(publicKey);
    authMainUserStore.clear();
  };

  const refreshToken = async () => {
    const { data: authData } = await api.refreshAuthToken();
    if (!authData) return;

    authStore.setAccessToken(authData.accessToken);

    const payload = extractTokenPayload<AccessTokenPayload>(authData.accessToken);
    if (payload.tenantOwners) {
      authStore.setTenantOwners(payload.tenantOwners);
    }

    if (payload.status === 'INACTIVE') {
      await appLogout();
    }
  };

  return {
    fetchAuthGrant,
    appLogin,
    finishLogin,
    finishRegistration,
    fetchGoogleAuth,
    refreshToken,
  };
};
